import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Button,
  ContactAttemptStack,
  ContactAttemptsModal,
  Input,
  Label,
  LeadPlatformCell,
  LeadStatusOptions,
  Modal,
  MultiSelect,
  Pagination,
  Select,
  Spinner,
  PHONE_COLUMN_IDS,
  RETRY_STATUSES,
  cn,
  computeLeadRange,
  formatAnswer,
  formatKstDateTime,
  formatPhone,
  pickPhone,
  statusColor,
  useAuth,
  useToast,
  type SourceMetaInfo,
} from "@toss-ads/ui";
import {
  createManualLead,
  deleteLeadAdmin,
  restoreLeadAdmin,
  fetchAdminAbsentLeads,
  fetchAdminLeads,
  fetchAdminMediaOptions,
  fetchAssignees,
  fetchSources,
  listAdvertisers,
  listPlatforms,
  removeContactAttemptAdmin,
  setLeadAssigneeAdmin,
  setLeadStatusAdmin,
  type AdminLead,
  type Assignee,
  type LeadStatus,
} from "../lib/api";
import { useUrlState } from "../lib/use-url-state";
import { useSearchField } from "../lib/use-search-field";
import { LEAD_FIELD_PRESETS } from "../lib/lead-field-presets";

const PER_PAGE = 20;
type DatePreset = "recent-7d" | "recent-30d" | "all" | "custom";

function isRetryStatus(s: LeadStatus): boolean {
  return (RETRY_STATUSES as readonly string[]).includes(s);
}

// 검색 입력 — 인풋은 로컬 state(한글 IME 조합 안 깨지게), URL 반영은 디바운스.
// committed 는 URL 동기화된(=디바운스된) 값으로 쿼리/필터에 사용.
export function AdvertiserLeadsSection({
  advertiserId,
  variant = "leads",
}: {
  advertiserId: string;
  variant?: "leads" | "absent";
}) {
  const absent = variant === "absent";
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const isStaff = user?.role === "admin" || user?.role === "developer";
  const [addOpen, setAddOpen] = React.useState(false);
  // 필터·페이지는 URL 쿼리에 동기화 (뒤로가기/새로고침 복원).
  // 부재 탭은 잠재고객 탭과 URL 파라미터가 겹치지 않게 접두어를 붙임.
  const p = absent ? "a." : "";
  // media: 콤마로 여러 매체 키(빈 값 = 전체).
  const [media, setMedia] = useUrlState(`${p}media`, "");
  const selectedMedia = React.useMemo(
    () => (media ? media.split(",").filter(Boolean) : []),
    [media],
  );
  const [presetRaw, setPreset] = useUrlState(`${p}date`, "recent-30d");
  const preset = presetRaw as DatePreset;
  const [customFrom, setCustomFrom] = useUrlState(`${p}from`, "");
  const [customTo, setCustomTo] = useUrlState(`${p}to`, "");
  const [statusRaw, setStatusFilter] = useUrlState(`${p}status`, "");
  const statusFilter = statusRaw as "" | LeadStatus;
  const [assigneeFilter, setAssigneeFilter] = useUrlState(`${p}assignee`, "");
  // 뷰: "" 실데이터(false) | "test" 테스트만 | "all" 전체 | "deleted" 삭제됨
  const [testFilter, setTestFilter] = useUrlState(`${p}test`, "");
  const deletedParam = testFilter === "deleted";
  const isTestParam = deletedParam
    ? undefined
    : testFilter === "test"
      ? true
      : testFilter === "all"
        ? undefined
        : false;
  // 인입 DB 검색 — 인풋은 로컬(IME), URL/쿼리는 디바운스.
  const nameField = useSearchField(`${p}name`);
  const contactField = useSearchField(`${p}contact`);
  const debouncedName = nameField.committed;
  const debouncedContact = contactField.committed;
  const [pageStr, setPageStr] = useUrlState(`${p}page`, "1");
  const page = Number(pageStr) || 1;
  const setPage = React.useCallback(
    (n: number) => setPageStr(String(n)),
    [setPageStr],
  );

  const range = React.useMemo(
    () => computeLeadRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  // 필터가 "실제로 바뀌면" 1페이지로. 최초 마운트(새로고침 포함)엔 URL 의 page 보존.
  // firstRender ref 가드는 StrictMode(이펙트 2회)에서 새로고침 시 page 가 날아가므로,
  // 이전 필터 시그니처와 비교해 값이 바뀐 경우에만 리셋한다.
  const filterSig = [
    preset,
    customFrom,
    customTo,
    statusFilter,
    assigneeFilter,
    testFilter,
    advertiserId,
    media,
    debouncedName,
    debouncedContact,
  ].join("\u0001");
  const prevFilterSig = React.useRef(filterSig);
  React.useEffect(() => {
    if (prevFilterSig.current === filterSig) return;
    prevFilterSig.current = filterSig;
    setPage(1);
  }, [filterSig, setPage]);

  // 전체 매체 목록 — platform_id → 이름/로고 매핑표. 집계(실데이터)만으로 만들면
  // 테스트 리드만 있는 매체가 이름을 못 찾아 원문으로 뜨므로, 전체 목록을 기준으로 한다.
  const { data: allPlatforms } = useQuery({
    queryKey: ["admin", "platforms", "all"],
    queryFn: listPlatforms,
  });

  const { data: mediaOptions } = useQuery({
    queryKey: ["admin", "leads", advertiserId, "media-options", range],
    queryFn: () =>
      fetchAdminMediaOptions(advertiserId, {
        from: range.from,
        to: range.to,
      }),
  });

  const { data: assignees } = useQuery({
    queryKey: ["admin", "assignees", advertiserId],
    queryFn: () => fetchAssignees(advertiserId),
  });

  const { data: sources } = useQuery({
    queryKey: ["leads", "sources"],
    queryFn: fetchSources,
  });
  const sourceMeta = React.useMemo(() => {
    const m = new Map<string, SourceMetaInfo>();
    for (const s of sources ?? []) m.set(s.slug, { label: s.label });
    return m;
  }, [sources]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      "admin",
      "leads",
      variant,
      advertiserId,
      media,
      range,
      absent ? "" : statusFilter,
      assigneeFilter,
      absent ? "" : testFilter,
      debouncedName,
      debouncedContact,
      page,
    ],
    queryFn: () => {
      const params = {
        advertiserId,
        media: selectedMedia.length ? media : undefined,
        from: range.from,
        to: range.to,
        // 부재 탭은 서버가 부재/단선으로 강제 → status 전달 안 함.
        status: absent ? undefined : statusFilter || undefined,
        assignee: assigneeFilter || undefined,
        name: debouncedName || undefined,
        contact: debouncedContact || undefined,
        // 부재 탭은 실데이터만. 그 외엔 is_test 뷰 필터 반영.
        isTest: absent ? false : isTestParam,
        // 삭제됨 뷰 — soft-delete 된 리드만 노출.
        deleted: absent ? undefined : deletedParam || undefined,
        limit: PER_PAGE,
        offset: (page - 1) * PER_PAGE,
      };
      return absent ? fetchAdminAbsentLeads(params) : fetchAdminLeads(params);
    },
  });

  const total = data?.total ?? 0;
  const customRangeValid =
    preset !== "custom" || (!!customFrom && !!customTo);

  const grandTotal = (mediaOptions ?? []).reduce((acc, o) => acc + o.total, 0);

  // platformId → {name, logoUrl} 메타. 전체 매체 목록 기준(+ 자연유입/organic).
  const platformMeta = React.useMemo(() => {
    const m = new Map<string, { name: string; logoUrl: string | null }>();
    for (const p of allPlatforms ?? [])
      m.set(p.id, { name: p.name, logoUrl: p.logoUrl });
    m.set("organic", { name: "자연유입", logoUrl: null });
    return m;
  }, [allPlatforms]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="text-[14px] font-semibold tracking-tight">
          {absent ? "부재 고객" : "잠재고객"}
          <span className="ml-2 text-[12px] text-ink-subtle tabular-nums">
            ({total.toLocaleString()})
          </span>
        </h2>
        {!absent && isStaff && (
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
            + 리드 추가
          </Button>
        )}
      </div>
      {!absent && (
        <ManualLeadModal
          advertiserId={advertiserId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
        />
      )}
      {absent && (
        <p className="text-[12px] text-ink-subtle mb-3 -mt-1">
          부재·단선·지속부재·상담후 부재·문발완료 상태이면서 연락 시도 7회
          미만인 고객. 상태가 바뀌거나 7회에 도달하면 목록에서 자동 제외됩니다.
        </p>
      )}

      <div className="rounded-lg border bg-surface p-5 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <MultiSelect
            allLabel={`매체 전체 (${grandTotal})`}
            options={(mediaOptions ?? []).map((o) => ({
              value: o.key,
              label: `${o.label} (${o.total})`,
            }))}
            selected={selectedMedia}
            onChange={(next) => setMedia(next.join(","))}
          />

          <Select
            value={preset}
            onChange={(e) => setPreset(e.target.value as DatePreset)}
          >
            <option value="recent-7d">최근 7일</option>
            <option value="recent-30d">최근 30일</option>
            <option value="all">전체 기간</option>
            <option value="custom">날짜 지정</option>
          </Select>

          {preset === "custom" && (
            <>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-[160px] h-[34px]"
              />
              <span className="text-[12px] text-ink-subtle">~</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-[160px] h-[34px]"
              />
            </>
          )}

          {!absent && (
            <Select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as "" | LeadStatus)
              }
            >
              <option value="">처리 상태 전체</option>
              <LeadStatusOptions />
            </Select>
          )}

          <Select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          >
            <option value="">담당자 전체</option>
            <option value="unassigned">미배정</option>
            {(assignees ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.note || a.email}
              </option>
            ))}
          </Select>

          {!absent && (
            <Select
              value={testFilter}
              onChange={(e) => setTestFilter(e.target.value)}
            >
              <option value="">실데이터</option>
              <option value="test">테스트</option>
              <option value="all">전체(실+테스트)</option>
              <option value="deleted">삭제됨</option>
            </Select>
          )}

          <Input
            value={nameField.input}
            onChange={(e) => nameField.setInput(e.target.value)}
            placeholder="이름 검색"
            className="w-[130px] h-[34px]"
          />
          <Input
            value={contactField.input}
            onChange={(e) => contactField.setInput(e.target.value)}
            placeholder="연락처 검색"
            className="w-[140px] h-[34px]"
          />
        </div>

        {preset === "custom" && !customRangeValid && (
          <p className="text-[11px] text-ink-subtle mt-2">
            시작일과 종료일을 모두 선택해주세요.
          </p>
        )}
      </div>

      <div className="rounded-lg border bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted">
            <tr>
              <Th width={170}>제출 시각</Th>
              <Th width={176}>플랫폼</Th>
              <Th width={150}>상태</Th>
              <Th width={130}>연락 시도</Th>
              <Th width={200}>담당자</Th>
              <Th width={160}>휴대전화</Th>
              <Th>응답</Th>
              <Th width={110}>SMS 알림</Th>
              <Th width={64}>관리</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="py-16">
                  <div className="flex justify-center text-ink-subtle">
                    <Spinner size={20} />
                  </div>
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={9} className="py-12 text-center text-[13px] text-danger">
                  {error instanceof ApiError
                    ? error.message
                    : "데이터를 불러오지 못했습니다."}
                </td>
              </tr>
            )}
            {!isLoading && !isError && total === 0 && (
              <tr>
                <td colSpan={9} className="py-12 text-center text-[13px] text-ink-subtle">
                  선택한 조건에 해당하는 lead 가 없습니다.
                </td>
              </tr>
            )}
            {data?.items.map((l) => (
              <LeadRow
                key={l.id}
                lead={l}
                meId={meId}
                assignees={assignees ?? []}
                platformMeta={platformMeta}
                sourceMeta={sourceMeta}
                deleted={deletedParam}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center mt-4">
        <Pagination
          total={total}
          perPage={PER_PAGE}
          current={page}
          onChange={setPage}
        />
      </div>
    </div>
  );
}

function LeadRow({
  lead,
  meId,
  assignees,
  platformMeta,
  sourceMeta,
  deleted,
}: {
  lead: AdminLead;
  meId: string | null;
  assignees: Assignee[];
  platformMeta: Map<string, { name: string; logoUrl: string | null }>;
  sourceMeta: Map<string, SourceMetaInfo>;
  deleted?: boolean;
}) {
  const phone = pickPhone(lead);
  const qc = useQueryClient();
  const toast = useToast();
  const mutate = useMutation({
    mutationFn: (status: LeadStatus) => setLeadStatusAdmin(lead.id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "leads"] });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "상태 변경 실패"),
  });
  const assign = useMutation({
    mutationFn: (assigneeId: string | null) =>
      setLeadAssigneeAdmin(lead.id, assigneeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "leads"] }),
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "배정 실패"),
  });
  const [attemptsOpen, setAttemptsOpen] = React.useState(false);
  const removeAttempt = useMutation({
    mutationFn: (at: string) => removeContactAttemptAdmin(lead.id, at),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "leads"] }),
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "삭제 실패"),
  });
  // +1 재시도는 별도 mutation — 상태셀 스피너(레이아웃 이동) 안 건드림.
  const retry = useMutation({
    mutationFn: () => setLeadStatusAdmin(lead.id, lead.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "leads"] }),
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "재시도 실패"),
  });
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [smsOpen, setSmsOpen] = React.useState(false);
  const remove = useMutation({
    mutationFn: () => deleteLeadAdmin(lead.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "leads"] });
      toast.success("리드를 삭제했습니다");
      setConfirmDelete(false);
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "삭제 실패"),
  });
  const [confirmRestore, setConfirmRestore] = React.useState(false);
  const restore = useMutation({
    mutationFn: () => restoreLeadAdmin(lead.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "leads"] });
      qc.invalidateQueries({ queryKey: ["admin", "advertisers", "summaries"] });
      toast.success("리드를 복구했습니다");
      setConfirmRestore(false);
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "복구 실패"),
  });
  return (
    <tr className="border-t border-border align-top">
      <Td className="text-ink">{formatKstDateTime(lead.leadSubmitTime)}</Td>
      <Td>
        <LeadPlatformCell
          source={lead.source}
          platformId={lead.platformId}
          channel={lead.channel}
          platformMeta={platformMeta}
          sourceMeta={sourceMeta}
        />
      </Td>
      <Td>
        <div className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: statusColor(lead.status) }}
            aria-hidden
          />
          <Select
            value={lead.status}
            onChange={(e) => mutate.mutate(e.target.value as LeadStatus)}
            disabled={mutate.isPending}
            className="w-[130px]"
          >
            <LeadStatusOptions />
          </Select>
          {/* 스피너 자리 항상 예약 — 나타났다 사라질 때 레이아웃 안 밀림. */}
          <span className="w-3 shrink-0 flex items-center">
            {mutate.isPending && <Spinner size={12} />}
          </span>
        </div>
      </Td>
      <Td>
        {lead.contactAttempts.length > 0 || isRetryStatus(lead.status) ? (
          <div className="flex flex-col gap-1 items-start">
            {lead.contactAttempts.length > 0 && (
              <button
                type="button"
                className="cursor-pointer hover:opacity-80"
                title="시도 내역 보기"
                onClick={() => setAttemptsOpen(true)}
              >
                <ContactAttemptStack
                  attempts={lead.contactAttempts}
                  showLastTime
                />
              </button>
            )}
            {isRetryStatus(lead.status) && (
              <button
                type="button"
                className="text-[11px] text-primary hover:underline disabled:opacity-50"
                disabled={retry.isPending}
                onClick={() => retry.mutate()}
              >
                +1 재시도
              </button>
            )}
          </div>
        ) : (
          <span className="text-ink-subtle text-[12px]">—</span>
        )}
        <ContactAttemptsModal
          open={attemptsOpen}
          onClose={() => setAttemptsOpen(false)}
          attempts={lead.contactAttempts}
          meId={meId}
          onDelete={(at) => removeAttempt.mutate(at)}
          deletingAt={removeAttempt.isPending ? removeAttempt.variables : null}
        />
      </Td>
      <Td>
        <div className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: lead.assigneeColor ?? "#e4e4e7" }}
            aria-hidden
          />
          <Select
            value={lead.assigneeId ?? ""}
            onChange={(e) => assign.mutate(e.target.value || null)}
            disabled={assign.isPending}
            className="w-[180px]"
          >
            <option value="">미배정</option>
            {assignees.map((a) => (
              <option key={a.id} value={a.id}>
                {a.note || a.email}
              </option>
            ))}
          </Select>
          {assign.isPending && <Spinner size={12} />}
        </div>
      </Td>
      <Td>
        {phone ? (
          <a
            href={`tel:${phone}`}
            className="font-mono text-[13px] text-primary hover:underline no-underline"
          >
            {formatPhone(phone)}
          </a>
        ) : (
          <span className="text-ink-subtle text-[12px]">—</span>
        )}
      </Td>
      <Td>
        <SubmittedFull lead={lead} />
      </Td>
      <Td>
        {lead.smsRecipients.length > 0 ? (
          <button
            type="button"
            className="text-[12px] text-primary hover:underline tabular-nums"
            onClick={() => setSmsOpen(true)}
            title="SMS 전송 내역 보기"
          >
            전송 {lead.smsRecipientCount}/{lead.smsRecipients.length}
          </button>
        ) : (
          <span className="text-[12px] text-ink-subtle">대상 없음</span>
        )}
        <LeadSmsModal
          open={smsOpen}
          onClose={() => setSmsOpen(false)}
          sentCount={lead.smsRecipientCount}
          recipients={lead.smsRecipients}
        />
      </Td>
      <Td>
        {deleted ? (
          <>
            <button
              type="button"
              className="text-[12px] text-primary hover:underline"
              onClick={() => setConfirmRestore(true)}
            >
              복구
            </button>
            <Modal
              open={confirmRestore}
              onClose={() => setConfirmRestore(false)}
              title="리드 복구"
              description="이 리드를 삭제됨에서 원래 목록으로 되돌립니다."
            >
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="text"
                  size="md"
                  onClick={() => setConfirmRestore(false)}
                >
                  취소
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  loading={restore.isPending}
                  onClick={() => restore.mutate()}
                >
                  복구
                </Button>
              </div>
            </Modal>
          </>
        ) : (
          <>
            <button
              type="button"
              className="text-[12px] text-danger hover:underline"
              onClick={() => setConfirmDelete(true)}
            >
              삭제
            </button>
            <Modal
              open={confirmDelete}
              onClose={() => setConfirmDelete(false)}
              title="리드 삭제"
              description="이 리드를 삭제 목록으로 옮깁니다. 삭제됨에서 복구할 수 있습니다."
            >
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="text"
                  size="md"
                  onClick={() => setConfirmDelete(false)}
                >
                  취소
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  loading={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  삭제
                </Button>
              </div>
            </Modal>
          </>
        )}
      </Td>
    </tr>
  );
}

function LeadSmsModal({
  open,
  onClose,
  sentCount,
  recipients,
}: {
  open: boolean;
  onClose: () => void;
  sentCount: number;
  recipients: Array<{ name: string; phone: string }>;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="SMS 전송 내역"
      description={`대상 ${recipients.length}명 중 ${sentCount}명에게 전송되었습니다.`}
    >
      <div className="max-h-[360px] overflow-y-auto -mx-1">
        {recipients.length === 0 ? (
          <div className="py-8 text-center text-[13px] text-ink-subtle">
            전송 대상이 없습니다.
          </div>
        ) : (
          <ol className="flex flex-col">
            {recipients.map((r, i) => (
              <li
                key={i}
                className="flex items-center gap-2.5 px-1 py-2 border-b border-border last:border-b-0"
              >
                <span className="text-[11px] text-ink-subtle tabular-nums w-5 shrink-0">
                  {i + 1}
                </span>
                <span className="text-[13px] text-ink flex-1 min-w-0 truncate">
                  {r.name}
                </span>
                <span className="text-[12px] text-ink-muted tabular-nums shrink-0">
                  {r.phone}
                </span>
                <span
                  className={cn(
                    "text-[11px] shrink-0 w-12 text-right",
                    sentCount > 0 ? "text-success" : "text-ink-subtle",
                  )}
                >
                  {sentCount > 0 ? "전송됨" : "미전송"}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
      {recipients.length > 0 && sentCount === 0 && (
        <p className="text-[11px] text-ink-subtle pt-3">
          아직 전송되지 않았습니다 (SMS 미설정 또는 발송 실패).
        </p>
      )}
      <div className="flex justify-end pt-4 mt-2 border-t border-border">
        <button
          type="button"
          onClick={onClose}
          className="text-[13px] text-ink-muted hover:text-ink px-3 py-1.5"
        >
          닫기
        </button>
      </div>
    </Modal>
  );
}

function SubmittedFull({ lead }: { lead: AdminLead }) {
  const ucd = (
    Array.isArray(lead.userColumnData)
      ? (lead.userColumnData as UserColumn[])
      : []
  ).filter(
    (c) =>
      !c?.column_id ||
      !PHONE_COLUMN_IDS.includes(c.column_id.toLowerCase()),
  );
  const items = Array.isArray(lead.submittedContent)
    ? (lead.submittedContent as Array<{ question?: string; answer?: unknown }>)
    : [];
  if (ucd.length === 0 && items.length === 0) {
    return <span className="text-[12px] text-ink-subtle">응답 없음</span>;
  }
  return (
    <div className="space-y-2">
      {ucd.map((c, idx) => (
        <div key={`u${idx}`} className="text-[12px]">
          <div className="text-ink-subtle whitespace-pre-wrap break-words">
            {c.column_name ?? c.column_id ?? "필드"}
          </div>
          <div className="text-ink whitespace-pre-wrap break-words mt-0.5">
            {c.string_value == null || c.string_value === ""
              ? "—"
              : String(c.string_value)}
          </div>
        </div>
      ))}
      {items.map((q, idx) => (
        <div key={`q${idx}`} className="text-[12px]">
          <div className="text-ink-subtle whitespace-pre-wrap break-words">
            Q. {q.question ?? `질문${idx + 1}`}
          </div>
          <div className="text-ink whitespace-pre-wrap break-words mt-0.5">
            {formatAnswer(q.answer)}
          </div>
        </div>
      ))}
    </div>
  );
}

interface UserColumn {
  column_id?: string;
  column_name?: string;
  string_value?: string | number | null;
}

function Th({
  children,
  width,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={cn("px-2.5 py-3.5 align-middle", className)}>{children}</td>
  );
}


// 리드 직접 추가 모달 — 광고주 컨텍스트 안에서 수동 입력. 채널은 선택형(기본값 없음).
function ManualLeadModal({
  advertiserId,
  open,
  onClose,
}: {
  advertiserId: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [channel, setChannel] = React.useState("");
  const [submittedAt, setSubmittedAt] = React.useState("");
  const [rows, setRows] = React.useState<Array<{ key: string; value: string }>>(
    [],
  );

  const advertisers = useQuery({
    queryKey: ["admin", "advertisers"],
    queryFn: listAdvertisers,
    enabled: open,
  });
  const slug = advertisers.data?.find((a) => a.id === advertiserId)?.slug;

  React.useEffect(() => {
    if (open) {
      setName("");
      setPhone("");
      setEmail("");
      setChannel("");
      setSubmittedAt("");
    }
  }, [open]);

  // 광고주 slug 를 알게 되면(쿼리 완료) 프리셋 행을 깔아준다. 프리셋 없으면 빈 목록.
  React.useEffect(() => {
    if (open && slug) {
      const preset = LEAD_FIELD_PRESETS[slug] ?? [];
      setRows(preset.map((k) => ({ key: k, value: "" })));
    }
  }, [open, slug]);

  const platforms = useQuery({
    queryKey: ["admin", "platforms", "all"],
    queryFn: listPlatforms,
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () => {
      const answers: Record<string, string> = {};
      for (const r of rows) {
        const k = r.key.trim();
        const v = r.value.trim();
        if (k && v) answers[k] = v;
      }
      return createManualLead({
        advertiserId,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        channel: channel || undefined,
        submittedAt: submittedAt
          ? new Date(submittedAt).toISOString()
          : undefined,
        answers: Object.keys(answers).length ? answers : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "leads"] });
      toast.success("리드를 추가했습니다");
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "추가 실패"),
  });

  const valid = name.trim().length > 0 && phone.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="리드 직접 추가"
      description="상담 신청 1건을 직접 입력합니다. 기존 리드와 동일하게 처리되고 SMS 알림이 발송됩니다."
    >
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">

        <div>
          <Label htmlFor="ml-name">이름</Label>
          <Input
            id="ml-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="ml-phone">연락처</Label>
          <Input
            id="ml-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="01012345678"
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="ml-email">이메일 (선택)</Label>
          <Input
            id="ml-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="ml-channel">채널 (매체)</Label>
          <Select
            id="ml-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
          >
            <option value="">선택…</option>
            {(platforms.data ?? []).map((p) => (
              <option key={p.id} value={p.slug}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="ml-submitted">신청 시각 (선택, 기본 현재)</Label>
          <Input
            id="ml-submitted"
            type="datetime-local"
            value={submittedAt}
            onChange={(e) => setSubmittedAt(e.target.value)}
          />
        </div>
        <div className="pt-2 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <Label>상담 항목 (선택)</Label>
            <Button
              variant="text"
              size="sm"
              onClick={() => setRows((r) => [...r, { key: "", value: "" }])}
            >
              + 항목 추가
            </Button>
          </div>
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={row.key}
                  onChange={(e) =>
                    setRows((r) =>
                      r.map((x, j) =>
                        j === i ? { ...x, key: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="항목명"
                  className="flex-1"
                />
                <Input
                  value={row.value}
                  onChange={(e) =>
                    setRows((r) =>
                      r.map((x, j) =>
                        j === i ? { ...x, value: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="값"
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => setRows((r) => r.filter((_, j) => j !== i))}
                  className="text-ink-subtle hover:text-danger px-1.5 text-[13px]"
                  aria-label="항목 삭제"
                >
                  ✕
                </button>
              </div>
            ))}
            {rows.length === 0 && (
              <p className="text-[12px] text-ink-subtle">
                필요하면 항목명·값을 추가하세요.
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button variant="text" size="md" onClick={onClose}>
          취소
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={!valid}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          추가
        </Button>
      </div>
    </Modal>
  );
}
